import styled, { createGlobalStyle } from "styled-components";
import { motion } from "framer-motion";

const colors = {
  primary: "#0052CC",
  background: "#f8f9fa",
  cardBackground: "#FFFFFF",
  textPrimary: "#172B4D",
  textSecondary: "#6B778C",
  border: "#DFE1E6",
  success: "#28a745",
  danger: "#dc3545",
};

const bp = {
  xl: 1400,
  lg: 1200,
  md: 992,
  sm: 768,
  xs: 576,
  xxs: 420,
};

export const GridWrapper = styled(motion.div)`
  background-color: ${colors.cardBackground};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  box-shadow:
    0 4px 8px -2px rgba(9, 30, 66, 0.1),
    0 0 1px rgba(9, 30, 66, 0.08);
  overflow: hidden;
  margin: 0 24px 24px 24px;

  @media (max-width: ${bp.sm}px) {
    margin: 0 16px 16px 16px;
  }
`;

export const GridHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.border};
  background-color: ${colors.cardBackground};

  @media (max-width: ${bp.sm}px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 18px 16px;
  }
`;

export const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: ${colors.textPrimary};

  @media (max-width: ${bp.sm}px) {
    font-size: 20px;
  }
`;

export const DataGridContainer = styled.div`
  background-color: ${colors.cardBackground};
  padding: 8px;
  overflow-x: auto;

  .dx-datagrid {
    min-width: 920px;
    color: ${colors.textPrimary};
  }

  .dx-datagrid-borders > .dx-datagrid-headers,
  .dx-datagrid-borders > .dx-datagrid-rowsview,
  .dx-datagrid-borders > .dx-datagrid-total-footer {
    border-right: 1px solid #DFE1E6;
    border-bottom: 1px solid #DFE1E6;
  }

  .dx-datagrid-header-panel {
    border-bottom: 1px solid ${colors.border};
    padding: 12px 16px;
    background-color: ${colors.cardBackground};
  }

  .dx-toolbar-items-container {
    gap: 12px;
  }

  .dx-datagrid-add-button {
    background-color: ${colors.primary};
    color: white;
    border: none;
    border-radius: 8px;
    padding: 0 16px;
    font-size: 14px;
    font-weight: 600;
    transition:
      background-color 0.2s ease-in-out,
      box-shadow 0.2s ease;

    &:hover {
      background-color: #0041a3;
      box-shadow: 0 4px 12px rgba(0, 82, 204, 0.2);
    }
  }

  .dx-datagrid-search-panel {
    border: 1px solid ${colors.border};
    border-radius: 8px;
    background: #fff;
    overflow: hidden;

    input {
      color: ${colors.textPrimary};
      font-size: 14px;
    }
  }

  .dx-datagrid-headers {
    border-bottom: 1px solid ${colors.border};

    .dx-header-row > td {
      color: #000;
      font-weight: 700;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      border: none;
      border-bottom: 1px solid ${colors.border};
      padding: 14px 12px;
      background-color: ${colors.cardBackground};
      vertical-align: middle;
    }
  }

  .dx-datagrid-filter-row td {
    border: none;
    border-bottom: 1px solid ${colors.border};
    background: #fff;
    padding: 8px 12px;
  }

  .dx-editor-cell .dx-texteditor,
  .dx-editor-cell .dx-selectbox {
    border-radius: 8px;
  }

  .dx-data-row {
    height: 52px;
    border-bottom: 1px solid ${colors.border} !important;
    color: ${colors.textPrimary};

    &:hover td {
      background-color: #f0f8ff;
    }

    td {
      vertical-align: middle;
      padding: 14px 12px;
      border: none;
      border-bottom: 1px solid ${colors.border};
      background-color: ${colors.cardBackground};
    }
  }

  .dx-row-alt td {
    background-color: #fbfcfe;
  }

  .dx-data-row:last-child {
    border-bottom: none !important;
  }

  .dx-command-edit a {
    color: ${colors.textSecondary};
    font-size: 16px;
    margin-right: 8px;

    &:hover {
      color: ${colors.primary};
    }
  }

  .dx-datagrid-pager,
  .dx-pager {
    padding: 12px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    border-top: 1px solid ${colors.border};
    background-color: ${colors.cardBackground};

    @media (max-width: ${bp.sm}px) {
      justify-content: center;
    }
  }

  .dx-page-info {
    color: ${colors.textSecondary};
    font-size: 14px;
  }

  .dx-navigate-button {
    border: none !important;
  }
`;

export const ActionCellWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

export const KebabButton = styled.button`
  background: transparent;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f1f3f5;
  }

  svg {
    color: ${colors.textSecondary};
  }
`;

export const ActionMenu = styled(motion.div)`
  position: absolute;
  right: 50px;
  top: 50%;
  transform: translateY(-50%);
  background-color: white;
  border: 1px solid ${colors.border};
  border-radius: 8px;
  box-shadow:
    0 8px 16px -4px rgba(9, 30, 66, 0.25),
    0 0 2px rgba(9, 30, 66, 0.08);
  padding: 8px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ActionMenuItem = styled.button<{ destructive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  background: transparent;
  border: none;
  padding: 10px 12px;
  width: 140px;
  text-align: left;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${colors.textPrimary};
  transition:
    background-color 0.2s ease,
    color 0.2s ease;

  svg {
    font-size: 1.1rem;
  }

  &:hover {
    background-color: ${({ destructive }) =>
    destructive ? "#FFEBE6" : "#DEEBFF"};
    color: ${({ destructive }) =>
    destructive ? colors.danger : colors.primary};
  }
`;

export const DetailContainer = styled.div`
  background-color: ${colors.cardBackground};
  padding: 24px 40px;

  @media (max-width: ${bp.sm}px) {
    padding: 20px 16px;
  }
`;

export const Fieldset = styled.fieldset`
  border: 1px solid ${colors.border};
  border-radius: 12px;
  padding: 16px;
  margin: 0 0 16px 0;
  background: #fff;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

export const Legend = styled.legend`
  padding: 0 8px;
  font-weight: 600;
  font-size: 14px;
  color: ${colors.primary};
`;

export const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px 24px;
`;

export const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const DetailLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${colors.textSecondary};
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.2px;
`;

export const DetailValue = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: ${colors.textPrimary};
  word-break: break-word;
`;

export const DevExtremeModalGlobalStyles = createGlobalStyle`
  .dx-popup-wrapper .dx-popup-content {
    border-radius: 8px !important;
    box-shadow:
      0 8px 16px -4px rgba(9, 30, 66, 0.25),
      0 0 2px rgba(9, 30, 66, 0.08) !important;
    padding: 0 !important;
  }

  .dx-overlay-content .dx-popup-title {
    padding: 16px 24px;
    border-bottom: 1px solid rgba(0,0,0,0.1);
    background-color: #F8981D !important;

    .dx-toolbar-label {
      font-size: 18px !important;
      font-weight: 600 !important;
      color: white !important;
    }

    .dx-closebutton .dx-icon {
      color: white !important;
    }
  }

  .dx-popup-content .dx-form {
    padding: 24px;
    background-color: #FFFFFF;
  }

  .dx-form-item > .dx-field-item-label {
    font-weight: 600 !important;
  }

  .dx-popup-bottom {
    padding: 16px 24px;
    background-color: #FFFFFF !important;
  }
`;

export const FormContainer = styled.div`
  padding: 1rem;
`;